import React from "react";

import { DocumentData, WithFieldValue } from "firebase/firestore";
import { Alert, ButtonProps, Text } from "@chakra-ui/react";
import { DB_COLLECTION_NAMES } from "@constants";
import { FirebaseContext } from "@contexts";
import { useCustomToast } from "@hooks";
import {
  arrayUnion,
  collection,
  getDoc,
  getDocs,
  doc,
  orderBy,
  query,
  updateDoc,
  where,
} from "firebase/firestore";

import { ButtonModal, JsonForm, JsonFormControl } from "@components";
import { Module } from "@constants";

type AddCernUploadInfoModalProps = ButtonProps & {
  itemId: string;
};

// Want to parse text that might have commas within quotation marks
function parse(row: string) {
  let insideQuote = false;
  const entries = [];
  let entry: string[] = [];

  row.split("").forEach(function (character) {
    if (character === '"') {
      insideQuote = !insideQuote;
    } else {
      if (character == "," && !insideQuote) {
        entries.push(entry.join(""));
        entry = [];
      } else {
        entry.push(character);
      }
    }
  });
  entries.push(entry.join(""));
  return entries;
}

const readUploadedFileAsText = (inputFile: Blob) => {
  const temporaryFileReader = new FileReader();

  return new Promise<string>((resolve, reject) => {
    temporaryFileReader.onerror = () => {
      temporaryFileReader.abort();
      reject(new DOMException("Problem parsing input file."));
    };

    temporaryFileReader.onload = () => {
      resolve(temporaryFileReader.result as string);
    };
    temporaryFileReader.readAsText(inputFile);
  });
};

const AddCernUploadInfoFormFields: JsonFormControl[] = [
  {
    label: "CernUploadInfoInput",
    inputType: "csv",
    fieldName: "csv",
    defaultValue: undefined,
  },
];

export function AddCernUploadInfoModal({
  itemId,
  ...delegated
}: AddCernUploadInfoModalProps) {
  const { firestore } = React.useContext(FirebaseContext);
  const collectionName = DB_COLLECTION_NAMES.MODULES;
  const collectionRef = collection(firestore, collectionName);

  const [error] = React.useState("");

  const tagAddedToast = useCustomToast(() => (
    <Alert status="success">Data from CSV added!</Alert>
  ));

  const tagNoModuleToast = useCustomToast(() => (
    <Alert status="success">Module does not exist -- no tags added</Alert>
  ));

  const tagExistsToast = useCustomToast(() => (
    <Alert status="success">Data from CSV already exists</Alert>
  ));

  const tagBadDataToast = useCustomToast(() => (
    <Alert status="success">
      Problem with data formatting -- skipping line
    </Alert>
  ));

  const doSubmit = React.useCallback(
    async function <T extends WithFieldValue<DocumentData>>(data: T) {
      const fileAsText = readUploadedFileAsText(data.csv);
      const allLines = (await fileAsText).split(/\r\n|\n/);

      // Get the list of tags for this particular file
      //const uploadTags = [];
      const uploadTags = new Array<string>();
      let isFirst = 1;
      const firstColumns = parse(allLines[0]);
      for (const tag of firstColumns) {
        if (!isFirst) {
          uploadTags.push(tag);
        }
        isFirst = 0;
      }

      isFirst = 1;
      for await (const line of allLines) {
        if (!line) continue;
        // Skip over the first line, since this should contain the information about which tags are being checked
        if (isFirst) {
          isFirst = 0;
          continue;
        }

        const columns = parse(line);
        if (columns.length != uploadTags.length + 1) {
          tagBadDataToast();
          continue;
        }
        const moduleId = columns[0];

        const moduleDoc = await getDoc(
          doc(firestore, collectionName, moduleId),
        );

        if (moduleDoc.data()) {
          const module = moduleDoc.data() as Module;
          if (!module) continue;
          await uploadTags.forEach(async (uploadTag, index) => {
            // Only add the tag if it has been uploaded to the CERN DB
            if (columns[index + 1] != "True" && columns[index + 1] != "TRUE")
              return;

            // Make sure the tag doesn't already exist in the DB
            if (
              module.queryTokens
                ? !module.queryTokens.includes("cernUpload:" + uploadTag)
                : true
            ) {
              const newQueryToken = "cernUpload:" + uploadTag;

              await updateDoc(moduleDoc.ref, {
                queryTokens: arrayUnion(newQueryToken),
                location: module.location.location,
                status: module.status,
              });
              tagAddedToast();
            } else {
              tagExistsToast();
            }
          });
        } else {
          const queryResult = query(
            collectionRef,
            orderBy("serials"),
            where("serials", "array-contains", moduleId),
          );
          const allModules = await getDocs(queryResult);

          allModules.forEach((doc) => {
            const module = doc.data() as Module;

            uploadTags.forEach(async (uploadTag, index) => {
              // Only add the tag if it has been uploaded to the CERN DB
              if (columns[index + 1] != "True" && columns[index + 1] != "TRUE")
                return;

              // Make sure the tag doesn't already exist in the DB
              if (
                module.queryTokens
                  ? !module.queryTokens.includes("cernUpload:" + uploadTag)
                  : true
              ) {
                const newQueryToken = "cernUpload:" + uploadTag;

                await updateDoc(doc.ref, {
                  queryTokens: arrayUnion(newQueryToken),
                  location: module.location.location,
                  status: module.status,
                });
                tagAddedToast();
              } else {
                tagExistsToast();
              }
            });
          });

          if (allModules.size < 1) {
            tagNoModuleToast();
          }
        }
      }
    },
    [itemId],
  );

  return (
    <ButtonModal
      label="Import DB Upload Info"
      title="Import DB Upload Info"
      size="sm"
      {...delegated}
    >
      <JsonForm
        fields={AddCernUploadInfoFormFields}
        onSubmit={doSubmit}
        clearAfterSubmit
      />
      {error ? <Text>{error}</Text> : undefined}
    </ButtonModal>
  );
}
